import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday
} from 'date-fns';
import { getAgendas, createAgenda, updateAgendaStatus } from '../services/api';
import AddAgendaForm from '../components/AddAgendaForm';
import { formatTime } from '../utils/timeFormat';
import { TEAM_COLORS, FULL_DAY_TEAMS, AGENDA_TEAMS, ATTENDING_TEAMS } from '../utils/constants';

const Calendar = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [agendas, setAgendas] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const fetchAgendas = async () => {
      try {
        setLoading(true);
        const fetchedAgendas = await getAgendas();
        const processedAgendas = fetchedAgendas.map(agenda => ({
          ...agenda,
          date: new Date(agenda.date)
        }));
        setAgendas(processedAgendas);
      } catch (error) {
        console.error('Error fetching agendas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgendas();
  }, []);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await fetch('https://libur.deno.dev/api');
        if (!response.ok) {
          throw new Error('Failed to fetch holidays');
        }
        const data = await response.json();
        setHolidays(data);
      } catch (error) {
        console.error('Error fetching holidays:', error);
      }
    };

    fetchHolidays();
  }, []);



  const nextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const prevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleApprove = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'approved');
      setAgendas(prev => prev.map(agenda =>
        (agenda.id === agendaId || agenda._id === agendaId) ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
    } catch (error) {
      console.error('Error approving agenda:', error);
    }
  };

  const handleReject = async (agendaId) => {
    try {
      const updatedAgenda = await updateAgendaStatus(agendaId, 'rejected');
      setAgendas(prev => prev.map(agenda =>
        (agenda.id === agendaId || agenda._id === agendaId) ? { ...updatedAgenda, date: new Date(updatedAgenda.date) } : agenda
      ));
    } catch (error) {
      console.error('Error rejecting agenda:', error);
    }
  };

  const onDateClick = (day) => {
    setSelectedDate(day);
  };

  const isHoliday = (day) => {
    return holidays.some(holiday => {
      const holidayDate = new Date(holiday.date);
      return isSameDay(day, holidayDate);
    }) || day.getDay() === 0 || day.getDay() === 6;
  };

  const getHolidayName = (day) => {
    if (day.getDay() === 0 || day.getDay() === 6) {
      return '';
    }
    const holiday = holidays.find(holiday => {
      const holidayDate = new Date(holiday.date);
      return isSameDay(day, holidayDate);
    });
    return holiday ? holiday.name : '';
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-light text-slate-800 tracking-tight">
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={prevMonth}
            className="p-2 rounded-full border border-slate-200/60 bg-white/50 backdrop-blur-sm text-slate-600 hover:bg-white hover:text-slate-800 transition-all duration-300 shadow-sm"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 rounded-full border border-slate-200/60 bg-white/50 backdrop-blur-sm text-slate-600 hover:bg-white hover:text-slate-800 transition-all duration-300 shadow-sm"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              if (user) {
                setShowAddModal(true);
              } else {
                navigate('/login');
              }
            }}
            className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-slate-800 hover:bg-slate-700 transition-all duration-300 shadow-sm shadow-slate-800/20"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Tambah Agenda
          </button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

    return (
      <div className="grid grid-cols-7 gap-px bg-slate-200/50 border border-slate-200/60 rounded-t-2xl overflow-hidden backdrop-blur-md">
        {days.map((day, index) => {
          const isWeekend = index === 0 || index === 6;
          return (
            <div key={day} className={`p-3 text-center ${isWeekend ? 'bg-rose-50/80' : 'bg-rose-50/80'}`}>
              <span className={`text-xs font-semibold uppercase tracking-wider ${isWeekend ? 'text-red-500' : 'text-red-500'}`}>{day}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayAgendas = agendas.filter(agenda => isSameDay(agenda.date, day));
        const holidayName = getHolidayName(day);
        const holiday = isHoliday(day);

        const isFullDayEvent = dayAgendas.some(agenda => FULL_DAY_TEAMS.includes(agenda.team));
        const fullDayColorClass = isFullDayEvent ? 'bg-amber-50/70 border-amber-200/50' : '';

        days.push(
          <div
            key={day}
            className={`min-h-[120px] border-r border-b p-3 cursor-pointer transition-all duration-300 ${holiday ? 'bg-rose-50/80 border-rose-200/60 hover:bg-rose-100/70' :
              isFullDayEvent ? fullDayColorClass :
                'bg-white/70 border-slate-200/50 hover:bg-white/90'
              } ${!isSameMonth(day, monthStart) ? 'opacity-50' : ''
              } ${isSameDay(day, selectedDate) ? 'ring-2 ring-inset ring-primary-300' : ''} ${isToday(day) && !isFullDayEvent && !holiday ? 'bg-primary-50/30' : ''
              }`}
            onClick={() => onDateClick(cloneDay)}
            title={holiday ? holidayName : ''}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium w-8 h-8 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-primary-600 text-white shadow-sm shadow-primary-600/30' : 'text-slate-700'
                }`}>
                {format(day, 'd')}
              </span>
              {isToday(day) && (
                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
              )}
            </div>
            <div className="space-y-1.5">
              {dayAgendas.slice(0, 3).map(agenda => {
                const teamColor = TEAM_COLORS[agenda.team] || TEAM_COLORS.default;
                return (
                  <div
                    key={agenda.id}
                    className={`text-xs p-1.5 rounded-md truncate border ${teamColor} shadow-sm backdrop-blur-sm bg-opacity-80`}
                    title={`${agenda.title} - ${formatTime(agenda.time)}`}
                  >
                    <span className="font-semibold opacity-70 mr-1">{formatTime(agenda.time)}</span>
                    {agenda.title}
                  </div>
                );
              })}
              {dayAgendas.length > 3 && (
                <div className="text-xs text-slate-500 font-medium px-1">
                  +{dayAgendas.length - 3} agenda lainnya
                </div>
              )}
              {holiday && (
                <div className="text-xs text-red-500 font-semibold mt-1 truncate" title={holidayName}>
                  {holidayName}
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }

    return <div className="glass-panel rounded-t-none border-t-0">{rows}</div>;
  };

  const getSelectedDateAgendas = () => {
    return agendas.filter(agenda => isSameDay(agenda.date, selectedDate));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {renderHeader()}

      {/* Make calendar full width and selected date details below */}
      <div className="flex flex-col gap-6">
        {/* Calendar */}
        <div>
          {renderDays()}
          {renderCells()}
        </div>

        {/* Selected Date Details */}
        <div className="glass-panel p-8">
          <h3 className="text-xl font-medium text-slate-800 mb-6 tracking-tight">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>

          {getSelectedDateAgendas().length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-sm font-medium text-slate-800">Tidak ada agenda</h3>
              <p className="mt-1 text-sm text-slate-500">
                Belum ada agenda pada tanggal ini.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => {
                    if (user) {
                      setShowAddModal(true);
                    } else {
                      navigate('/login');
                    }
                  }}
                  className="inline-flex items-center px-6 py-2.5 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 transition-all duration-300 hover:scale-[1.02] shadow-slate-800/20"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Tambah Agenda
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {getSelectedDateAgendas().map(agenda => (
                <div key={agenda.id} className="border border-slate-200/60 rounded-xl p-5 bg-white/40 hover:bg-white/60 transition-colors duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-medium text-slate-800 tracking-tight">
                        {agenda.title}
                      </h4>
                      <p className="text-[15px] text-slate-500 mt-1.5 leading-relaxed">
                        {agenda.description}
                      </p>
                      <div className="flex items-center mt-3 space-x-2">
                        <ClockIcon className="h-4 w-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-600">{formatTime(agenda.time)} - {formatTime(agenda.endTime)}</span>
                      </div>
                      <div className="flex items-center mt-2 flex-wrap gap-y-1">
                        <span className="text-xs text-slate-500 mr-2 flex items-center">
                          <span className="text-slate-400 mr-1">Diusulkan oleh</span>{AGENDA_TEAMS[agenda.team] || agenda.createdBy}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          Dihadiri oleh {ATTENDING_TEAMS[agenda.attendingTeam] || 'Tidak diketahui'}
                        </span>
                        {agenda.location && (
                          <>
                            <span className="text-xs text-gray-400 mx-2">•</span>
                            <span className="text-xs text-gray-500 flex items-center">
                              <MapPinIcon className="h-3 w-3 mr-1" />
                              {agenda.location}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium tracking-wide uppercase mt-1 ${getStatusColor(agenda.status)}`}>
                        {agenda.status}
                      </span>
                      {isAdmin && agenda.status === 'pending' && (
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleApprove(agenda.id || agenda._id); }}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-green-500 hover:bg-green-600 focus:outline-none transition-all duration-200"
                            title="Setujui"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleReject(agenda.id || agenda._id); }}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-500 hover:bg-red-600 focus:outline-none transition-all duration-200"
                            title="Tolak"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Agenda Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg glass-panel flex flex-col max-h-[90vh]">
            <div className="p-8 overflow-y-auto no-scrollbar">
              <h3 className="text-2xl font-light text-slate-800 mb-8 tracking-tight shrink-0">Tambah Agenda Baru</h3>
              <AddAgendaForm
                onSubmit={async (formData) => {
                  try {
                    await createAgenda(formData);
                    setShowAddModal(false);
                    const fetchedAgendas = await getAgendas();
                    const processedAgendas = fetchedAgendas.map(agenda => ({
                      ...agenda,
                      date: new Date(agenda.date)
                    }));
                    setAgendas(processedAgendas);
                  } catch (error) {
                    console.error('Failed to add agenda:', error);
                  }
                }}
                onCancel={() => setShowAddModal(false)}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Calendar;
